import { NoteTemplate } from '@eigtech/templates-types'
import { ContextualCan } from '@eigtech/ui-shared-auth'
import { ReactSelectField, useFormContext } from '@eigtech/ui-shared-forms/shadcn'
import { useRoutesContext } from '@eigtech/ui-shared-router'
import { useGetTemplates } from '@eigtech/ui-shared-templates'
import { Button } from '@eigtech/ui/components/button'
import { HStack } from '@eigtech/ui/components/stack'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { Link } from '@tanstack/react-router'
import { has, isEmpty, set, template } from 'lodash-es'
import { ExternalLinkIcon, RefreshCwIcon } from 'lucide-react'
import { NoteFieldsType } from './NoteFieldsSchema'
import { toast } from '@eigtech/ui/components/sonner'

export type NoteTemplateFieldProps = {
  templateParams?: Record<string, unknown>
}

export function NoteTemplateField(props: NoteTemplateFieldProps) {
  return (
    <ContextualCan I="read" a="template">
      <Component {...props} />
    </ContextualCan>
  )
}

function Component({ templateParams }: NoteTemplateFieldProps) {
  const form = useFormContext<NoteFieldsType>()
  const { templates: templatesPath } = useRoutesContext()

  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
    refetch: refetchTemplates,
  } = useGetTemplates({
    type: 'templates:types:note',
  })

  function handleTemplateSelected(selectedTemplate: NoteTemplate | null | undefined) {
    if (!selectedTemplate) return

    if (!('textTemplate' in selectedTemplate)) return

    if (!isEmpty(selectedTemplate.textTemplateParams)) {
      const validatedParams = { ...templateParams }

      selectedTemplate.textTemplateParams?.map((param) => {
        const found = has(validatedParams, param)
        if (!found) {
          set(validatedParams, param, `<%= ${param} %>`)
        }
      })

      const compiledTemplate = template(selectedTemplate.textTemplate)
      const compiledBody = compiledTemplate(validatedParams)

      form.setValue('text', compiledBody, { shouldValidate: true })
      return
    }

    form.setValue('text', selectedTemplate.textTemplate, { shouldValidate: true })
  }

  return (
    <HStack className="items-end">
      <ReactSelectField
        getOptionLabel={(template) => template.name}
        getOptionValue={(template) => template.id}
        isClearable
        isLoading={isLoadingTemplates}
        label="Note Template"
        name="template"
        options={templates as NoteTemplate[]}
        onChange={handleTemplateSelected}
      />

      <Tooltip>
        <TooltipTrigger
          aria-label="Refetch templates."
          render={<Button variant="outline" />}
          onClick={() => {
            refetchTemplates()
            toast.success('Refetched templates')
          }}
        >
          <RefreshCwIcon />
        </TooltipTrigger>

        <TooltipContent>
          <span>Refetch templates</span>
        </TooltipContent>
      </Tooltip>

      {templatesPath && (
        <Tooltip>
          <TooltipTrigger
            aria-label="Go to templates."
            render={
              <Button render={<Link target="templates" to={templatesPath} />} variant="outline" />
            }
          >
            <ExternalLinkIcon />
          </TooltipTrigger>

          <TooltipContent>
            <span>Go to templates</span>
          </TooltipContent>
        </Tooltip>
      )}
    </HStack>
  )
}
